import { useLanguage, LanguageSwitcher } from "./i18n";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { runCapabilityGate } from "../viewer/capability";
import type { ViewerTelemetry } from "../viewer/core";
import DimensionRanker from "./DimensionRanker";
import EvidencePanel, { ReferencePanel } from "./EvidencePanel";
import { EVIDENCE_GUIDANCE, type EvidenceMode } from "./evidence_guidance";
import SimulationPanel from "./SimulationPanel";
import type { CapabilityMessage } from "../api";
import { ApiError, candidateName, emptyTelemetry, interactionGap, request, type InteractionGap, type Orders, type Session, type Study, type Submission, type Telemetry, type Unit } from "./rank_api";
import "./rank.css";
import "./workbench.css";

const SESSION_KEY = "object_rank_session_v4";
function message(error: unknown): string { return error instanceof TypeError ? "网络连接失败，请检查网络后重试。" : error instanceof Error ? error.message : "操作失败，请重试"; }

export default function RankStudyPage() {
  const { t, tf } = useLanguage();
  const [studies, setStudies] = useState<Study[]>([]);
  const [session, setSession] = useState<Session | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [studyId, setStudyId] = useState("");
  const [code, setCode] = useState("");
  const [participant, setParticipant] = useState("");
  const [consent, setConsent] = useState(false);
  // Advisories from the device check. They never block; the participant sees them once and
  // confirms, so a slow machine is a warning to budget more time rather than a locked door.
  const [advisories, setAdvisories] = useState<CapabilityMessage[]>([]);
  useEffect(() => {
    void (async () => {
      try {
        const catalog = await request<Study[]>("/api/v2/studies");
        setStudies(catalog);
        const selected = new URLSearchParams(location.search).get("study");
        const requested = catalog.find((s) => s.study_id === selected)?.study_id;
        setStudyId(requested ?? (catalog.length === 1 ? catalog[0]!.study_id : ""));
        const sid = localStorage.getItem(SESSION_KEY);
        if (sid) {
          try { setSession(await request<Session>("/api/v2/session/" + encodeURIComponent(sid))); }
          catch (reason) {
            setError(message(reason));
            // A protocol change retires every session on the previous study, and a deleted
            // session never comes back. Keeping the id would show the same error on every
            // reload with no way out but clearing site data, so drop it and let the entry
            // form take over -- the message stays, explaining why they are starting again.
            if (reason instanceof ApiError
              && ["study_version_changed", "session_not_found"].includes(reason.code)) {
              localStorage.removeItem(SESSION_KEY);
            }
          }
        }
      } catch (reason) { setError(message(reason)); }
      finally { setBusy(false); }
    })();
  }, []);

  async function begin(event: FormEvent) {
    event.preventDefault();
    if (!consent || busy) return;
    setBusy(true); setError("");
    try {
      const capability = await runCapabilityGate();
      if (!capability.passed) {
        throw new Error(capability.reason ? tf(capability.reason.template, ...capability.reason.values)
          : t("设备检查未通过"));
      }
      if (capability.warnings.length && !advisories.length) {
        setAdvisories(capability.warnings);
        return;                       // show them once; the next submit goes through
      }
      const value = await request<Session>("/api/v2/session", {
        study_id: studyId, invite_code: code.trim(), participant_id: participant.trim(),
        capability: { webgl2: capability.webgl2, warmup_fps: capability.warmupFps,
          user_agent: navigator.userAgent, renderer: capability.renderer },
      });
      localStorage.setItem(SESSION_KEY, value.session_id);
      setSession(value);
    } catch (reason) { setError(message(reason)); }
    finally { setBusy(false); }
  }
  return <div className="rank-study">
    <header className="rs-topbar"><a href="/study" className="rs-brand"><span className="rs-brand-icon">▧</span>OBJECT STUDY</a>
      <div className="rs-topbar-actions">
        {session?.config.admin === true && <span className="rs-admin-banner" role="status" data-admin-banner>{t("ADMIN 预览模式 · 方法名已显示")}</span>}
        <LanguageSwitcher /></div></header>
    {session ? <SessionFlow session={session} onExit={() => { localStorage.removeItem(SESSION_KEY); setSession(null); }} /> :
      <main className="rs-entry">
        <div className="rs-entry-copy"><span className="rs-eyebrow">{t("交互式 3D 产物评测")}</span><h1>{t("好的物体，")}<br />{t("经得起亲手操作。")}</h1>
          <p>{t("对照参考图与描述，观察六个产物的外观，尝试操作它们，再选出最好的三个并排序。")}</p>
          <ol><li><span>01</span><div><strong>{t("观察细节")}</strong><p>{t("相同视角，比较形状、材质与部件。")}</p></div></li>
            <li><span>02</span><div><strong>{t("尝试操作")}</strong><p>{t("拖动部件，观察开合、联动与接触。")}</p></div></li>
            <li><span>03</span><div><strong>{t("排出前三")}</strong><p>{t("从六个产物中选出最好的三个并排序，其余不排名。")}</p></div></li></ol>
        </div>
        <form className="rs-entry-form" onSubmit={(event) => void begin(event)}>
          <span className="rs-eyebrow">{t("开始或继续")}</span><h2>{t("进入评测")}</h2>
          {studies.length > 1 && <label>{t("评测研究")}<select value={studyId} onChange={(event) => setStudyId(event.target.value)} disabled={busy}>
            <option value="">{t("请选择")}</option>
            {studies.map((study) => <option key={study.study_id} value={study.study_id}>{study.study_id} · {study.objects} {t("个物体")}</option>)}</select></label>}
          <label>{t("参与者代号")}<input name="participant-id" required value={participant} onChange={(event) => setParticipant(event.target.value)} autoComplete="off" /></label>
          <label>{t("邀请码（选填）")}<input name="invite-code" value={code} onChange={(event) => setCode(event.target.value)} autoComplete="off" /></label>
          <p className="rs-consent-copy">{t("我们会保存排名、操作时长与基础性能数据，用于学术研究。你可以随时停止，使用相同代号恢复进度。建议使用桌面电脑和鼠标。")}</p>
          <label className="rs-consent"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />{t("我已阅读说明，自愿参加研究")}</label>
          {error && <p className="rs-error" role="alert">{t(error)}</p>}
          {advisories.length > 0 && <div className="rs-advisory" role="status" data-device-advisory>
            <strong>{t("设备检查提示（不影响参加）")}</strong>
            <ul>{advisories.map((item) => <li key={item.template}>{tf(item.template, ...item.values)}</li>)}</ul></div>}
          <button className="rs-primary" type="submit" disabled={busy || !consent || !studyId}>
            {busy ? t("正在准备／检查设备…") : advisories.length ? t("仍然继续 →") : t("检查设备并进入 →")}</button>
          {!busy && !studies.length && <p className="rs-error">{t("当前没有可用评测数据，请联系组织者。")}</p>}
        </form>
      </main>}
  </div>;
}

function SessionFlow({ session, onExit }: { session: Session; onExit: () => void }) {
  const { t } = useLanguage();
  const [index, setIndex] = useState(session.next_unit_index);
  const [unit, setUnit] = useState<Unit | null>(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (index >= session.total_units) return;
    let cancelled = false; setUnit(null); setError("");
    void request<Unit>("/api/v2/session/" + session.session_id + "/unit/" + index).then((value) => {
      if (!cancelled) { if (value.submitted) setIndex((i) => i + 1); else setUnit(value); }
    }).catch((reason) => { if (!cancelled) setError(message(reason)); });
    return () => { cancelled = true; };
  }, [index, retry, session.session_id]);
  if (index >= session.total_units) return <main className="rs-complete" data-study-complete="true">
    <div className="rs-complete-mark">✓</div><span className="rs-eyebrow">{t("评测完成")}</span><h1>{t("感谢你的认真观察。")}</h1>
    <p>{t("所有已提交回答均已保存，可以安全关闭页面。")}</p><button type="button" className="rs-secondary" onClick={onExit}>{t("返回首页")}</button></main>;
  if (error) return <main className="rs-loading"><p role="alert">{t(error)}</p><button type="button" onClick={() => setRetry((n) => n + 1)}>{t("重试")}</button></main>;
  if (!unit) return <main className="rs-loading">{t("正在准备物体…")}</main>;
  return <RankUnit key={unit.unit_token} unit={unit} session={session} onAdvance={setIndex} />;
}

function RankUnit({ unit, session, onAdvance }: { unit: Unit; session: Session; onAdvance: (index: number) => void }) {
  const { t, tf } = useLanguage();
  const storageKey = "rank_draft:" + unit.unit_token;
  const pendingKey = "rank_pending:" + unit.unit_token;
  function initialOrders(): Orders {
    try {
      const pending = JSON.parse(localStorage.getItem(pendingKey) ?? "null") as Submission | null;
      if (pending) return pending.rankings;
      const saved = JSON.parse(localStorage.getItem(storageKey) ?? "null") as { revision: number; rankings: Orders } | null;
      if (saved && saved.revision >= unit.draft_revision) return saved.rankings;
    } catch { /* Ignore malformed local drafts; the server draft remains authoritative. */ }
    return unit.draft.rankings ?? {};
  }
  const [orders, setOrders] = useState<Orders>(initialOrders);
  const [dimensionIndex, setDimensionIndex] = useState(0);
  const [evidenceMode, setEvidenceMode] = useState<EvidenceMode>("2d");
  const evidenceScroll = useRef<HTMLDivElement>(null);
  const judgmentScroll = useRef<HTMLDivElement>(null);
  const [telemetry, setTelemetry] = useState<Record<string, Telemetry>>(() =>
    Object.fromEntries(unit.candidates.map((c) => [c.token, unit.draft.telemetry?.[c.token] ?? emptyTelemetry()])));
  const [saving, setSaving] = useState("草稿已恢复");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Set when another tab or window of this session has moved the unit on (a newer draft, or a
  // submission). Saving from this page can then only fail again, so it stops and says why.
  const [conflict, setConflict] = useState(false);
  const conflicted = useRef(false);
  const revision = useRef(unit.draft_revision);
  const ordersRef = useRef(orders); ordersRef.current = orders;
  const telemetryRef = useRef(telemetry); telemetryRef.current = telemetry;
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const lastSaved = useRef("");
  const mounted = useRef(true);
  const submittingRef = useRef(false);
  const frozen = useRef<Submission | null>(null);
  const config = session.config;
  // Only the owner's admin sessions carry method names; participants' payloads never do.
  const admin = config.admin === true;
  const dimension = config.dimensions[dimensionIndex]!;
  const single = config.dimensions.length === 1;
  const guidance = EVIDENCE_GUIDANCE[dimension.id]!;
  function showEvidence(mode: EvidenceMode) {
    setEvidenceMode(mode);
    evidenceScroll.current?.scrollTo({ top: 0 });
  }
  function chooseDimension(index: number) {
    setDimensionIndex(index);
    showEvidence(EVIDENCE_GUIDANCE[config.dimensions[index]!.id]!.mode);
    judgmentScroll.current?.scrollTo({ top: 0 });
  }
  const rankCount = config.rank_count;
  const dimensionCount = config.dimensions.length;
  const complete = (id: string) => orders[id]?.length === rankCount;
  const dimensionsComplete = config.dimensions.filter((d) => complete(d.id)).length;
  const gaps = unit.candidates
    .map((c) => interactionGap(c, telemetry[c.token]!, config.unlock, admin))
    .filter((gap): gap is InteractionGap => gap !== null);
  const allOperated = gaps.length === 0;
  function localSave() {
    localStorage.setItem(storageKey, JSON.stringify({ revision: revision.current, rankings: ordersRef.current }));
  }
  function isConflict(reason: unknown): boolean {
    return reason instanceof ApiError && (reason.code === "stale_draft" || reason.code === "out_of_order");
  }
  function markConflict() {
    conflicted.current = true;
    if (mounted.current) { setConflict(true); setSaving("草稿保存在本机"); setError("本题已在另一个页面打开，请刷新页面后继续。"); }
  }
  function saveDraft(): Promise<void> {
    const payload = { rankings: structuredClone(ordersRef.current), telemetry: structuredClone(telemetryRef.current) };
    const signature = JSON.stringify(payload);
    localSave();
    if (signature === lastSaved.current) return saveChain.current;
    saveChain.current = saveChain.current.catch(() => undefined).then(async () => {
      if (!mounted.current || submittingRef.current) return;
      setSaving("正在保存…");
      const result = await request<{ revision: number }>(
        "/api/v2/session/" + session.session_id + "/unit/" + unit.unit_index + "/draft",
        { revision: revision.current, ...payload }, "PUT");
      revision.current = result.revision; lastSaved.current = signature;
      localSave(); if (mounted.current) setSaving("草稿已保存");
    });
    return saveChain.current;
  }
  useEffect(() => {
    mounted.current = true;
    const interval = window.setInterval(() => {
      if (!submittingRef.current && !conflicted.current) void saveDraft().catch((reason) => {
        if (isConflict(reason)) markConflict();
        else if (mounted.current) { setSaving("草稿保存在本机"); setError(message(reason)); }
      });
    }, 2000);
    return () => { mounted.current = false; clearInterval(interval); };
  }, []);
  useEffect(() => { localSave(); }, [orders]);

  function updateOrders(value: string[]) {
    if (submitting) return;
    frozen.current = null; localStorage.removeItem(pendingKey);
    setOrders((previous) => ({ ...previous, [dimension.id]: value }));
  }
  function updateTelemetry(token: string, value: ViewerTelemetry) {
    setTelemetry((previous) => ({
      ...previous, [token]: { ...(previous[token] ?? emptyTelemetry()),
        active_ms: value.activeMs, drag_count: value.dragCount, reset_count: value.resetCount,
        fps: value.fps, simulation_fault_count: value.simulationFaultCount ?? 0, load_status: "ready" },
    }));
  }
  function viewed(token: string, view: string) {
    setTelemetry((previous) => {
      const item = previous[token] ?? emptyTelemetry();
      if (item.viewed_images.includes(view)) return previous;
      return { ...previous, [token]: { ...item, viewed_images: [...item.viewed_images, view] } };
    });
  }
  /** Say what is missing. A submit that quietly does nothing is the whole complaint. */
  function blocker(): string {
    const join = (items: string[]) => items.join(t("、"));
    if (dimensionsComplete !== dimensionCount) {
      if (single) return t("请先选出最好的三个并排好顺序。");
      const pending = config.dimensions.filter((d) => !complete(d.id)).map((d) => t(d.label));
      return tf("还差这些维度没排完：{0}。每个维度要选出并排列前三名。", join(pending));
    }
    if (!gaps.length) return "";
    const drags = config.unlock.min_drags_per_asset;
    const seconds = Math.round(config.unlock.min_active_ms_per_asset / 1000);
    // Spelling out every candidate is only readable for a few; past that it is the wall of
    // text this message exists to replace, so name them and state the rule once.
    if (gaps.length > 3) {
      return tf("还差 {0} 个产物没操作够：{1}。每个产物要操作 {2} 次、查看 {3} 秒：按住左键拖动模型本体，转动视角不计数。",
        gaps.length, join(gaps.map((gap) => gap.label)), drags, seconds);
    }
    const parts = gaps.map((gap) => {
      // A candidate never opened in 3D is short of everything; saying so once is enough.
      const needs = gap.loading ? [t("还没在 3D 里打开")] : [
        gap.operations ? tf("再操作 {0} 次", gap.operations) : "",
        gap.seconds ? tf("再看 {0} 秒", gap.seconds) : "",
      ].filter(Boolean);
      return tf("产物 {0}（{1}）", gap.label, join(needs));
    });
    return tf("还差这些产物没操作够：{0}。每个产物要操作 {1} 次、查看 {2} 秒：按住左键拖动模型本体，转动视角不计数。",
      join(parts), drags, seconds);
  }
  async function submit() {
    if (submitting) return;
    // Nothing from this page can be saved any more; any other message would hide that.
    if (conflicted.current) { markConflict(); return; }
    const blocked = blocker();
    if (blocked) {
      setError(blocked);
      if (!allOperated && evidenceMode !== "3d") showEvidence("3d");
      return;
    }
    setSubmitting(true); setError("");
    try {
      await saveDraft();
      submittingRef.current = true;
      if (!frozen.current) {
        const saved = localStorage.getItem(pendingKey);
        frozen.current = saved ? JSON.parse(saved) as Submission : {
          session_id: session.session_id, unit_token: unit.unit_token, request_id: crypto.randomUUID(),
          rankings: structuredClone(ordersRef.current), telemetry: structuredClone(telemetryRef.current),
        };
        localStorage.setItem(pendingKey, JSON.stringify(frozen.current));
      }
      const result = await request<{ next_unit_index: number }>("/api/v2/rank", frozen.current);
      localStorage.removeItem(storageKey); localStorage.removeItem(pendingKey);
      await new Promise((resolve) => window.setTimeout(resolve, config.cooldown_ms));
      onAdvance(result.next_unit_index);
    } catch (reason) {
      setError(message(reason));
      if (reason instanceof ApiError && reason.code === "submission_conflict") setError("此题已经提交，请刷新页面恢复最新进度。");
      if (isConflict(reason)) markConflict();
      setSubmitting(false); submittingRef.current = false;
    }
  }
  return <main className="rs-unit rs-workbench" data-unit-token={unit.unit_token} data-unit-index={unit.unit_index}>
    <div className="rs-progress"><span>{unit.is_practice ? t("练习 · 不计入结果") : t("正式评测 · ") + (unit.unit_index - config.practice_objects + 1) + " / " + config.scored_objects_per_session}</span>
      <div><i style={{ width: ((unit.unit_index / session.total_units) * 100) + "%" }} /></div><small>{t(saving)}</small></div>
    {unit.is_practice && single && <p className="rs-practice-note" data-practice-note>{tf(
      "练习题，不计分：六个产物在 3D 里各操作 {0} 次、各看 {1} 秒，再选出前三名并排序，即可进入正式评测。",
      config.unlock.min_drags_per_asset, Math.round(config.unlock.min_active_ms_per_asset / 1000))}</p>}
    <div className="rs-workbench-grid">
    <section className="rs-evidence-pane" aria-label={t("证据区")}>
    <ReferencePanel unit={unit} />
    <div className="rs-evidence-toolbar">
      <div role="tablist" aria-label={t("证据类型")} className="rs-evidence-tabs">
        <button type="button" role="tab" id="evidence-tab-2d" aria-controls="evidence-panel-2d" aria-selected={evidenceMode === "2d"}
          data-evidence-tab="2d" onClick={() => showEvidence("2d")}>{t("2D 渲染图")}</button>
        <button type="button" role="tab" id="evidence-tab-3d" aria-controls="evidence-panel-3d" aria-selected={evidenceMode === "3d"}
          data-evidence-tab="3d" onClick={() => showEvidence("3d")}>{t("3D 交互")}</button>
      </div>
      {!single && <span>{t("当前判断")} · {t(dimension.label)}</span>}
    </div>
    <div className="rs-evidence-scroll" ref={evidenceScroll}>
      <div role="tabpanel" id="evidence-panel-2d" aria-labelledby="evidence-tab-2d" hidden={evidenceMode !== "2d"}>
        <EvidencePanel unit={unit} onViewed={viewed} admin={admin} />
      </div>
      <div role="tabpanel" id="evidence-panel-3d" aria-labelledby="evidence-tab-3d" hidden={evidenceMode !== "3d"}>
        <SimulationPanel candidates={unit.candidates} telemetry={telemetry} onTelemetry={updateTelemetry} unlock={config.unlock}
          practice={unit.is_practice} active={evidenceMode === "3d"} admin={admin} />
      </div>
    </div>
    <div className="rs-interaction-status">{unit.candidates.map((c) => {
      const item = telemetry[c.token]!;
      const gap = interactionGap(c, item, config.unlock, admin);
      // Count down from the gap, not from the raw totals: resets alone raise the total but
      // never satisfy the rule, and a bar reading 2/2 beside a hollow mark is worse than none.
      const operations = config.unlock.min_drags_per_asset - (gap?.operations ?? 0);
      return <span key={c.token} className={gap === null ? "ready" : ""} data-telemetry-candidate={c.label}
        data-drag-count={item.drag_count} data-reset-count={item.reset_count}
        data-active-ms={item.active_ms} data-load-status={item.load_status}>{gap === null ? "✓" : "○"} {candidateName(c, admin)}
        <small>{tf("{0}/{1} 次操作 · {2} 秒", operations, config.unlock.min_drags_per_asset,
          Math.min(Math.floor(item.active_ms / 1000), config.unlock.min_active_ms_per_asset / 1000))}</small></span>;
    })}</div>
    </section>
    <aside className="rs-judgment-pane" aria-label={t("排名区")}>
    <div className="rs-judgment-scroll" ref={judgmentScroll}>
    <section className="rs-ranking">
      <div className="rs-section-heading"><div><h2>{t(single ? "综合质量排名" : "对照证据，逐维排名")}</h2>
        <p>{t(single ? "选出最好的三个并排序，第 1 名最好。" : "每个维度独立判断，选出最好的三个并排序，第 1 名最好。")}</p></div>
        {!single && <span className="rs-counter">{dimensionsComplete} / {dimensionCount} {t("完成")}</span>}</div>
      {!single && <div className="rs-dimension-tabs" role="tablist">{config.dimensions.map((d, index) =>
        <button key={d.id} type="button" role="tab" aria-selected={index === dimensionIndex}
          data-dimension-tab={d.id} className={index === dimensionIndex ? "selected" : ""} disabled={submitting}
          onClick={() => chooseDimension(index)}>{complete(d.id) ? "✓ " : ""}{t(d.label)}</button>)}</div>}
      {!single && <div className="rs-evidence-advice" data-guidance-dimension={dimension.id}>
        <span>{t("主要参考")} · {t(guidance.source)}</span>
        <p>{t(guidance.instruction)}</p>
        {evidenceMode !== guidance.mode && <button type="button" onClick={() => showEvidence(guidance.mode)}>{t("查看推荐证据")} →</button>}
      </div>}
      <DimensionRanker dimension={dimension} candidates={unit.candidates} order={orders[dimension.id] ?? []}
        rankCount={rankCount} onChange={updateOrders} disabled={submitting} admin={admin} />
      {!single && <div className="rs-dimension-footer">
        <button className="rs-secondary" type="button" disabled={dimensionIndex === 0 || submitting} onClick={() => chooseDimension(dimensionIndex - 1)}>{t("← 上一个维度")}</button>
        <span>{dimensionIndex + 1} / {dimensionCount}</span>
        {dimensionIndex < dimensionCount - 1 && <button className="rs-secondary" type="button" disabled={!complete(dimension.id) || submitting} onClick={() => chooseDimension(dimensionIndex + 1)}>{t("下一个维度 →")}</button>}</div>}
    </section>
    {!single &&     <details className={"rs-review" + (admin ? " rs-admin" : "")}><summary>{t("查看六维排名总览")} · {dimensionsComplete} / {dimensionCount}</summary><div className="rs-review-grid">{config.dimensions.map((d, index) =>
      <button type="button" key={d.id} disabled={submitting} onClick={() => chooseDimension(index)}>
        <span>{t(d.label)}</span><strong>{complete(d.id) ? orders[d.id]!.map((token) => candidateName(unit.candidates.find((c) => c.token === token)!, admin)).join("  ›  ") : t("待完成")}</strong></button>)}</div></details>}
    </div>
    <footer className="rs-judgment-footer">
      {error && <p className="rs-error" role="alert">{t(error)}{conflict && <> <button type="button" data-reload-page
        onClick={() => location.reload()}>{t("刷新页面")}</button></>}</p>}
      <div className="rs-submit-row"><p>{!allOperated
        ? tf("还有 {0} 个产物没操作够：{1}", gaps.length, gaps.map((gap) => gap.label).join(t("、")))
        : dimensionsComplete !== dimensionCount ? t(single ? "还需选出并排列前三名。" : "还需在每个维度选出并排列前三名。")
          : t(single ? "前三名已选好，提交后将进入下一个物体。" : "六个维度的前三名已完整，提交后将进入下一个物体。")}</p>
        {!allOperated && evidenceMode !== "3d" && <button className="rs-secondary" type="button" onClick={() => showEvidence("3d")}>{t("去操作六个产物")} →</button>}
        <button type="button" className="rs-primary" data-submit-rank disabled={submitting}
          data-submit-blocked={dimensionsComplete !== dimensionCount || !allOperated ? "true" : "false"}
          onClick={() => void submit()}>{submitting ? t("正在保存…") : unit.is_practice ? t("完成练习 →") : t("确认并提交 →")}</button></div>
    </footer>
    </aside>
    </div>
  </main>;
}
